// src/pages/GemReport.tsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { FunctionsHttpError } from "@supabase/supabase-js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Share2,
  ArrowLeft,
  Zap,
  Sparkles,
  Loader2,
  MessageCircle,
  Trash,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { PERSONAS_DATA } from "@/data/personas_data";
import { sanitizeRichHtmlOrPlainText, stripHtmlToText } from "@/lib/richText";

async function copyTextToClipboard(text: string): Promise<void> {
  const value = String(text ?? "");
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!ok) {
    throw new Error("Copy failed");
  }
}

// 模式：对外只有 spark / guided；历史上的 deep 统一视为 spark
type AiMode = "spark" | "guided" | null;

interface AiResponse {}

interface JournalRow {
  id: string;
  created_at: string;
  content: string;
  ai_mode: AiMode;
  ai_response: AiResponse | null;
   ai_hawkins_level?: number | null;
   ai_persona?: string | null;
}

function detectTextLanguage(text: string): "zh" | "en" {
  const sample = (text || "").slice(0, 4000);
  let cjk = 0;
  let latin = 0;
  for (const ch of sample) {
    const code = ch.charCodeAt(0);
    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3400 && code <= 0x4dbf) ||
      (code >= 0x3040 && code <= 0x30ff) ||
      (code >= 0xac00 && code <= 0xd7af)
    ) {
      cjk++;
    } else if ((code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a)) {
      latin++;
    }
  }

  if (cjk >= 20 && cjk > latin) return "zh";
  return "en";
}

function normalizePersonaNameToLanguage(
  name: string,
  desired: "zh" | "en",
): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return trimmed;

  const hasCjk = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/.test(trimmed);
  if (desired === "zh") {
    if (hasCjk) return trimmed;
    const lowered = trimmed.toLowerCase();
    for (const personaKey of Object.keys(PERSONAS_DATA)) {
      const persona: any = (PERSONAS_DATA as any)[personaKey];
      const en: string | undefined = persona?.englishName;
      if (en && en.trim().toLowerCase() === lowered) {
        return persona?.name || personaKey;
      }
    }
    return trimmed;
  }

  // desired === "en"
  if (!hasCjk) return trimmed;
  const direct: any = (PERSONAS_DATA as any)[trimmed];
  if (direct?.englishName) return String(direct.englishName);
  for (const personaKey of Object.keys(PERSONAS_DATA)) {
    const persona: any = (PERSONAS_DATA as any)[personaKey];
    const zh: string | undefined = persona?.name;
    if (zh && zh.trim() === trimmed && persona?.englishName) {
      return String(persona.englishName);
    }
  }
  return trimmed;
}

// 从任意对象中递归收集所有 string 文本
function collectStringsDeep(source: any): string[] {
  const result: string[] = [];

  const walk = (val: any) => {
    if (val == null) return;
    if (typeof val === "string") {
      const s = val.trim();
      if (s) result.push(s);
      return;
    }
    if (Array.isArray(val)) {
      val.forEach(walk);
      return;
    }
    if (typeof val === "object") {
      Object.values(val).forEach(walk);
    }
  };

  walk(source);
  return result;
}

function getPersonaForHawkins(
  bucket: string | null,
): { nameZh: string; nameEn: string; energyLabel: string } | null {
  if (!bucket) return null;
  let target: number | null = null;

  // 1) 尝试直接从字符串里抓数字（如 540, 310 等）
  const match = bucket.match(/\d+/);
  if (match) {
    const n = parseInt(match[0], 10);
    if (Number.isFinite(n)) target = n;
  }

  // 2) 如果没有数字，则根据中文关键词做一个大致映射
  if (target == null) {
    const normalized = bucket.replace(/\s+/g, "");
    if (normalized.includes("羞愧")) target = 20;
    else if (normalized.includes("罪恶") || normalized.includes("内疚")) target = 30;
    else if (normalized.includes("冷漠") || normalized.includes("麻木")) target = 50;
    else if (normalized.includes("悲伤") || normalized.includes("悲痛")) target = 75;
    else if (normalized.includes("恐惧")) target = 100;
    else if (normalized.includes("渴望") || normalized.includes("欲望")) target = 125;
    else if (normalized.includes("愤怒")) target = 150;
    else if (normalized.includes("骄傲") || normalized.includes("面子")) target = 175;
    else if (normalized.includes("勇气")) target = 200;
    else if (normalized.includes("意愿")) target = 310; // 对应实干家
    else if (normalized.includes("接受")) target = 350; // Hawkins 接受 ~ Reason，对应智者
    else if (normalized.includes("理性") || normalized.includes("理解")) target = 350;
    else if (normalized.includes("爱")) target = 500;
    else if (normalized.includes("喜悦")) target = 540;
    else if (normalized.includes("平和")) target = 600;
    else if (normalized.includes("开悟") || normalized.includes("合一")) target = 700;
  }

  // 2b) English keyword buckets (e.g., "Joy", "Love", "Fear")
  if (target == null) {
    const en = bucket.toLowerCase();
    if (en.includes("shame")) target = 20;
    else if (en.includes("guilt")) target = 30;
    else if (en.includes("apathy") || en.includes("numb")) target = 50;
    else if (en.includes("grief") || en.includes("sad")) target = 75;
    else if (en.includes("fear") || en.includes("anx")) target = 100;
    else if (en.includes("desire") || en.includes("crav") || en.includes("longing")) target = 125;
    else if (en.includes("anger") || en.includes("rage")) target = 150;
    else if (en.includes("pride")) target = 175;
    else if (en.includes("courage")) target = 200;
    else if (en.includes("willing")) target = 310;
    else if (en.includes("accept") || en.includes("reason") || en.includes("understand")) target = 350;
    else if (en.includes("love")) target = 500;
    else if (en.includes("joy")) target = 540;
    else if (en.includes("peace")) target = 600;
    else if (en.includes("enlight") || en.includes("oneness")) target = 700;
  }

  if (target == null) return null;
  if (!Number.isFinite(target)) return null;

  for (const personaKey of Object.keys(PERSONAS_DATA)) {
    const persona: any = (PERSONAS_DATA as any)[personaKey];
    const energy: string = persona?.energy || "";
    const digits = energy.split(/[·]/)[0];
    const parts = digits.split("/").map((p) => parseInt(p.trim(), 10));
    if (parts.some((n) => Number.isFinite(n) && n === target)) {
      return {
        nameZh: persona?.name || personaKey,
        nameEn: persona?.englishName || persona?.name || personaKey,
        energyLabel: energy,
      };
    }
  }
  return null;
}

export default function GemReport() {
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  // 删除弹窗与删除状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 删除日记
  const handleDelete = async () => {
    if (!entry) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("journals").delete().eq("id", entry.id);
      if (error) throw error;
      toast({
        title: t("gem.deleteSuccess", { defaultValue: "Deleted!" }),
        description: t("gem.deleteSuccessDesc", { defaultValue: "This Gem has been deleted." }),
      });
      navigate("/my-gems");
    } catch (err) {
      toast({
        title: t("gem.deleteFailed", { defaultValue: "Delete failed" }),
        description: t("gem.deleteFailedDesc", { defaultValue: "Could not delete this Gem. Please try again." }),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const [entry, setEntry] = useState<JournalRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // AI Insight 生成状态
  const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);
  const [showGenerateInsightDialog, setShowGenerateInsightDialog] = useState(false);

  // Copy share text: journal content + AI Insight text (when present)
  const shareText = useMemo(() => {
    if (!entry) return "";

    const content = stripHtmlToText(String((entry as any).content ?? "")).trim();

    const uiLang: "zh" | "en" = i18n.language?.startsWith("zh") ? "zh" : "en";

    const rawAi: unknown = (entry as any).ai_response;
    let ai: unknown = rawAi;
    if (typeof rawAi === "string") {
      try {
        ai = JSON.parse(rawAi);
      } catch {
        ai = rawAi;
      }
    }

    let insight = "";
    let metaEmotion: string | null = null;
    let metaHawkins: string | null = null;
    let metaPersona: string | null = null;
    let metaPersonaEnergy: string | null = null;

    if (ai && typeof ai === "object") {
      const { meta, ...rest } = ai as any;
      const allStrings = collectStringsDeep(rest)
        .map((s) => s.trim())
        .filter(Boolean);
      const unique = Array.from(new Set(allStrings));
      insight = unique.join("\n\n").trim();

      if (meta && typeof meta === "object") {
        const prim = (meta as any).primary_emotion;
        const hawk = (meta as any).hawkins_bucket;
        if (typeof prim === "string" && prim.trim()) {
          metaEmotion = prim.trim();
        }
        if (hawk != null && String(hawk).trim()) {
          metaHawkins = String(hawk).trim();
          const personaInfo = getPersonaForHawkins(metaHawkins);
          if (personaInfo) {
            metaPersona = personaInfo.nameZh;
            const digits = personaInfo.energyLabel.split(/[·]/)[0].trim();
            metaPersonaEnergy = digits;
          }
        }
      }
    } else if (typeof ai === "string") {
      insight = ai.trim();
    }

    const shareLang: "zh" | "en" = insight.trim() ? detectTextLanguage(insight) : uiLang;

    // If backend already wrote ai_hawkins_level / ai_persona, prefer DB values.
    const dbPersona = ((entry as any).ai_persona as string | null) || null;
    const dbHawkins = (entry as any).ai_hawkins_level as number | null;
    if (dbHawkins != null || dbPersona) {
      if (dbHawkins != null) {
        metaHawkins = String(dbHawkins);
        const fromDb = getPersonaForHawkins(String(dbHawkins));
        if (fromDb) {
          metaPersona = fromDb.nameZh;
          const digits = fromDb.energyLabel.split(/[·]/)[0].trim();
          metaPersonaEnergy = digits;
        } else if (dbPersona) {
          metaPersona = dbPersona;
          metaPersonaEnergy = String(dbHawkins);
        }
      } else if (dbPersona) {
        metaPersona = dbPersona;
      }
    }

    if (metaPersona) {
      metaPersona = normalizePersonaNameToLanguage(metaPersona, shareLang);
    }

    const labels =
      shareLang === "zh"
        ? {
            journal: "【日记】",
            insight: "【AI INSIGHT】",
            meta: "【Hawkins / Persona】",
            emotion: "主要情绪：",
            hawkins: "Hawkins：",
            persona: "内在人格：",
          }
        : {
            journal: "[Journal]",
            insight: "[AI Insight]",
            meta: "[Hawkins / Persona]",
            emotion: "Primary emotion: ",
            hawkins: "Hawkins: ",
            persona: "Inner persona: ",
          };

    const sections: string[] = [];
    if (content) sections.push(`${labels.journal}\n${content}`);
    if (insight) sections.push(`${labels.insight}\n${insight}`);

    const metaLines: string[] = [];
    if (metaEmotion) metaLines.push(`${labels.emotion}${metaEmotion}`);
    if (metaHawkins) metaLines.push(`${labels.hawkins}${metaHawkins}`);
    if (metaPersona) {
      metaLines.push(
        `${labels.persona}${metaPersona}${metaPersonaEnergy ? ` ${metaPersonaEnergy}` : ""}`,
      );
    }
    if (metaLines.length) sections.push(`${labels.meta}\n${metaLines.join("\n")}`);

    return sections.join("\n\n").trim();
  }, [entry, i18n.language]);

  const handleShareMyGem = async () => {
    if (!entry) return;

    const text = shareText;
    if (!text) {
      toast({
        title: t("gem.shareFailedTitle", { defaultValue: "Copy failed" }),
        description: t("gem.shareFailedDesc", { defaultValue: "Nothing to copy." }),
        variant: "destructive",
      });
      return;
    }

    try {
      await copyTextToClipboard(text);
      toast({
        title: t("gem.copiedTitle", { defaultValue: "Copied" }),
        description: t("gem.copiedDesc", { defaultValue: "Gem text copied to clipboard." }),
      });
    } catch (err) {
      console.error("Copy Gem text failed:", err);
      toast({
        title: t("gem.shareFailedTitle", { defaultValue: "Copy failed" }),
        description: t("gem.shareFailedDesc", { defaultValue: "We couldn’t copy this Gem just now." }),
        variant: "destructive",
      });
    }
  };

  // 生成 AI Insight

  const handleConfirmGenerateInsight = async () => {
    if (!entry) return;

    setIsGeneratingInsight(true);
    try {
      const modeForAi: AiMode = entry.ai_mode === "guided" ? "guided" : "spark";

      const resolveMaxCharsForUser = async (): Promise<number> => {
        try {
          const { data: auth } = await supabase.auth.getUser();
          const userId = auth?.user?.id;
          if (!userId) return 1000;

          const { data: profile } = await supabase
            .from("user_profiles")
            .select("has_lifetime_deal, subscription_tier")
            .eq("id", userId)
            .maybeSingle();

          const hasLifetimeDeal = !!(profile as any)?.has_lifetime_deal;
          const rawTier = ((profile as any)?.subscription_tier as string | null) || "";
          const tier = rawTier.toLowerCase().replace(/\s+/g, "_");

          const paidTiers = [
            "beginner",
            "intermediate",
            "upper_intermediate",
            "lifetime_deal",
            "beginner_monthly",
            "beginner_yearly",
            "intermediate_monthly",
            "intermediate_yearly",
            "upper_intermediate_monthly",
            "upper_intermediate_yearly",
          ];
          const hasPaidTier = tier ? paidTiers.includes(tier) : false;
          const hasPaidPlan = hasLifetimeDeal || hasPaidTier;

          return hasPaidPlan ? 3000 : 1000;
        } catch {
          return 1000;
        }
      };

      const maxChars = await resolveMaxCharsForUser();
      const content = stripHtmlToText(String(entry.content || ""));
      // Send only the last chunk to control cost and avoid backend 400 (limit_exceeded).
      const journalTextForAi =
        content.length > maxChars ? content.slice(content.length - maxChars) : content;

      const { data, error } = await supabase.functions.invoke("generate-ai-insight", {
        body: {
          journalId: entry.id,
          journalText: journalTextForAi,
          mode: modeForAi ?? "spark",
          stage: "full",
          emotion: null,
          userLanguage: i18n.language,
        },
      });

      if (error) {
        throw error;
      }

      if (data) {
        const payload: any = data;
        const { journalId: _ignoredJournalId, _meta, ...aiResult } = payload;

        setEntry((prev) =>
          prev
            ? {
                ...prev,
                ai_mode: (prev.ai_mode ?? modeForAi ?? "spark") as AiMode,
                ai_response: aiResult,
              }
            : prev,
        );

        toast({
          title: t("gem.insightSaved", { defaultValue: "Insight saved for this Gem" }),
        });
      }
    } catch (err: any) {
      console.error("Generate AI Insight failed:", err);

      let details = err?.message || "";
      if (err instanceof FunctionsHttpError) {
        const ctx: any = (err as any).context;
        const rawBody = ctx?.body;
        try {
          const parsed = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody;
          details = parsed?.message || parsed?.error || details;
        } catch {
          // ignore
        }
      }

      toast({
        title: t("common.error", { defaultValue: "Something went wrong" }),
        description:
          details ||
          t("gems.loadError", {
            defaultValue: "Something went wrong while contacting the AI.",
          }),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingInsight(false);
      setShowGenerateInsightDialog(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setErrorMsg("Invalid Gem ID.");
      setIsLoading(false);
      return;
    }

    const fetchEntry = async () => {
      setIsLoading(true);
      setErrorMsg(null);

      try {
        const { data, error } = await supabase
          .from("journals")
          .select("id, created_at, content, ai_mode, ai_response, ai_hawkins_level, ai_persona")
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error loading journal:", error);
          setErrorMsg("Failed to load this Gem. It may have been removed.");
          return;
        }

        if (!data) {
          setErrorMsg("Gem not found.");
          return;
        }

        setEntry(data as JournalRow);
      } catch (err) {
        console.error("Unexpected error loading journal:", err);
        setErrorMsg("Something went wrong while loading this Gem.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntry();
  }, [id]);

  if (isLoading) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t("gem.loading", { defaultValue: "Loading Gem…" })}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (errorMsg || !entry) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              {errorMsg || t("gem.notFound", { defaultValue: "Gem not found." })}
            </p>
            <Button onClick={() => navigate("/my-gems")}>
              {t("gem.backToMyGems", { defaultValue: "Back to My Gems" })}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const createdDate = new Date(entry.created_at);
  const aiMode = entry.ai_mode;
  const ai = entry.ai_response ?? {};

  // 解析 AI 结果用于展示：主文案 + 情绪标签
  const modeResolved: AiMode =
    (aiMode === "guided" || (ai as any)?.mode === "guided") ? "guided" : "spark";

  // Insight language should follow the actual AI Insight text (not UI i18n).
  let insightLang: "zh" | "en" = i18n.language?.startsWith("zh") ? "zh" : "en";

  let insightText = "";
  let metaEmotion: string | null = null;
  let metaHawkins: string | null = null;
  let metaPersona: string | null = null;
  let metaPersonaEnergy: string | null = null;
  const dbPersona = (entry as any).ai_persona as string | null;
  const dbHawkins = (entry as any).ai_hawkins_level as number | null;
  if (ai && typeof ai === "object") {
    const root: any = ai;
    const { meta, ...rest } = root || {};

    const allStrings = collectStringsDeep(rest)
      .map((s) => s.trim())
      .filter(Boolean); // 保留所有非空文本，由后端决定长短

    const unique = Array.from(new Set(allStrings));
    insightText = unique.join("\n\n");

    if (insightText.trim()) {
      insightLang = detectTextLanguage(insightText);
    }

    if (meta && typeof meta === "object") {
      const prim = (meta as any).primary_emotion;
      const hawk = (meta as any).hawkins_bucket;
      if (typeof prim === "string" && prim.trim()) {
        metaEmotion = prim.trim();
      }
      if (hawk != null && String(hawk).trim()) {
        metaHawkins = String(hawk).trim();
        const personaInfo = getPersonaForHawkins(metaHawkins);
        if (personaInfo) {
          metaPersona = personaInfo.nameZh;
          const digits = personaInfo.energyLabel.split(/[·]/)[0].trim();
          metaPersonaEnergy = digits;
        }
      }
    }
  }


  // 如果后端已经在 journals 表里写好了 ai_hawkins_level / ai_persona，则以数据库为准
  if (dbHawkins != null || dbPersona) {
    if (dbHawkins != null) {
      metaHawkins = String(dbHawkins);
      const fromDb = getPersonaForHawkins(String(dbHawkins));
      if (fromDb) {
        metaPersona = fromDb.nameZh;
        const digits = fromDb.energyLabel.split(/[·]/)[0].trim();
        metaPersonaEnergy = digits;
      } else if (dbPersona) {
        metaPersona = dbPersona;
        metaPersonaEnergy = String(dbHawkins);
      }
    } else if (dbPersona) {
      metaPersona = dbPersona;
    }
  }

  if (metaPersona) {
    metaPersona = normalizePersonaNameToLanguage(metaPersona, insightLang);
  }
  const noInsightText =
    modeResolved === "spark"
      ? t("gem.noSpark", {
          defaultValue:
            "Click Generate AI Insight to receive a reflection for this Gem.",
        })
      : t("gem.noGuided", {
          defaultValue:
            "Guided mode creates a deeper reflection once per Gem. When you feel ready, tap Generate AI Insight.",
        });

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* 顶部：返回 + 标题 + 日期 + 操作按钮 */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">
              {t("gem.title", { defaultValue: "My Inner Gem" })}
            </h1>
            <p className="mt-1 text-xs text-muted-foreground">
              {createdDate.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShareMyGem}
            title={t("gem.share", { defaultValue: "Copy" })}
          >
            <Share2 className="h-5 w-5" />
          </Button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-2 hover:bg-red-100 text-red-500 hover:text-red-700 transition"
            title={t("gem.delete", { defaultValue: "Delete" })}
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* 删除确认弹窗 */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("gem.deleteConfirmTitle", {
                defaultValue: "是否删除此日记？",
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-muted-foreground">
            {t("gem.deleteConfirmDesc", {
              defaultValue: "删除后无法恢复，确定要删除这条日记吗？",
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              {t("common.cancel", { defaultValue: "取消" })}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting
                ? t("common.deleting", { defaultValue: "正在删除..." })
                : t("common.delete", { defaultValue: "删除" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reflection card */}
      <Card className="shadow-[var(--shadow-soft)] mb-6">
        <CardHeader>
          <CardTitle>
            {t("gem.reflection", { defaultValue: "Your Reflection" })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="text-foreground text-sm leading-relaxed break-words"
            dangerouslySetInnerHTML={{
              __html: sanitizeRichHtmlOrPlainText(String(entry.content || "")),
            }}
          />
        </CardContent>
      </Card>

      {/* AI Insight 卡片 */}
      <Card className="shadow-[var(--shadow-soft)] mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {t("gem.insight", { defaultValue: "AI Insight" })}
          </CardTitle>
          {entry.ai_response && (
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-[11px] px-2 py-0.5 rounded-full"
              >
                {t("gem.insightSaved", {
                  defaultValue: "Insight saved for this Gem",
                })}
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {entry.ai_response && insightText ? (
            <>
              <div className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {insightText}
              </div>
              {(metaPersona || metaHawkins) && (
                <div className="mt-3 text-xs text-muted-foreground space-y-1 whitespace-pre-wrap">
                  {metaPersona ? (
                    <p>
                      {t("gem.aiHawkinsLabel", {
                        lng: insightLang,
                        defaultValue:
                          insightLang === "en"
                            ? "Inner Persona Match: "
                            : "用户内在人格对应：",
                      })}
                      {metaPersona}
                      {metaPersonaEnergy ? ` ${metaPersonaEnergy}` : ""}
                    </p>
                  ) : null}

                  {!metaPersona && metaHawkins ? (
                    <p>
                      {insightLang === "en" ? "Hawkins level: " : "能量层级："}
                      {metaHawkins}
                    </p>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {noInsightText}
            </p>
          )}

          {!entry.ai_response && (
            <div className="mt-4 flex justify-end">
              <Button
                size="sm"
                onClick={() => setShowGenerateInsightDialog(true)}
                disabled={isGeneratingInsight}
              >
                {isGeneratingInsight && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isGeneratingInsight
                  ? t("gem.generating", { defaultValue: "Generating…" })
                  : t("gem.generateBtn", {
                      defaultValue: "Generate AI Insight",
                    })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 生成 AI Insight 确认弹窗 */}
      <Dialog
        open={showGenerateInsightDialog}
        onOpenChange={(open) => {
          if (!isGeneratingInsight) setShowGenerateInsightDialog(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("gem.generateTitle", {
                defaultValue: "Generate AI Insight for this Gem?",
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground space-y-2">
            <p>
              {t("gem.generateDesc1", {
                defaultValue:
                  "We'll gently ask the Inner Gem AI to reflect on this entry and attach a one-time Insight to your Gem.",
              })}
            </p>
            <p>
              {t("gem.generateDesc2", {
                defaultValue:
                  "Once generated, the Insight will be permanently saved with this Gem and cannot be regenerated again. If the AI is busy, your Gem stays safe — you can come back tomorrow to try again.",
              })}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowGenerateInsightDialog(false)}
              disabled={isGeneratingInsight}
            >
              {t("common.cancel", { defaultValue: "取消" })}
            </Button>
            <Button
              onClick={handleConfirmGenerateInsight}
              disabled={isGeneratingInsight}
            >
              {isGeneratingInsight ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("gem.generating", { defaultValue: "Generating…" })}
                </>
              ) : (
                t("gem.generateConfirm", {
                  defaultValue: "Yes, generate Insight",
                })
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
